/**
 * viewGetTyped — Pillar 3 typed reader for SL view-proxy slugs.
 *
 * Wraps the existing `viewGet` (which returns `ViewResponse<T>` without
 * shape validation) with a zod `safeParse` against the per-surface wire
 * envelope. On parse failure throws `ContractMismatchError` populated
 * with `{slug, expectedShape, actualShape, rawData}` so downstream
 * consumers (Sentry, UI banners, tests) can pinpoint the drift.
 *
 * D-33 R1: NEVER returns on safeParse failure. NEVER swallows. Always
 * throws.
 *
 * Per ADR-CROSS-SYSTEM-CONTRACTS-V1 v4 §2.2 Pillar 3 + LIB-CARD-SL-CXSC
 * §2 "Pillar 2 enum acceptance ratchet" + §2 "endpoint-binding
 * regression" reusable patterns.
 *
 * Usage:
 *   const { data, total, meta } = await viewGetTyped(
 *     'chat-inbox',
 *     ChatInboxEnvelopeSchema,
 *     { filters: { lane: 'eq.konversation' }, limit: 50 },
 *   )
 */

import type { z } from 'zod'
import { viewGet, type ViewGetOptions } from './viewProxyClient'
import { ContractMismatchError } from './slErrors'

export interface TypedViewResponse<T> {
  data: T[]
  total: number
  /** Opaque per L5 — canonical shape is `passthrough().optional()`. */
  meta?: unknown
}

/**
 * Typed view-proxy reader. Authors a zod envelope per surface and runs
 * `safeParse` against the SL response.
 *
 * @typeParam T inferred from `envelopeSchema` (the item type inside `data[]`).
 * @param slug view-proxy slug (e.g. `'chat-inbox'`).
 * @param envelopeSchema per-surface zod envelope (use `viewProxyEnvelope`).
 * @param opts pagination/filter/signal options forwarded to `viewGet`.
 *
 * @throws `ContractMismatchError` on safeParse failure (D-33 R1).
 */
export async function viewGetTyped<T>(
  slug: string,
  // Input is `unknown`: the schema's INPUT type is irrelevant here (the raw body is
  // unknown until parsed), and pinning it to the output type refused any schema that
  // transforms a row (the active-questionnaire envelope tags each row with its shape).
  envelopeSchema: z.ZodType<{ data: T[]; total: number; meta?: unknown }, z.ZodTypeDef, unknown>,
  opts: ViewGetOptions = {},
): Promise<TypedViewResponse<T>> {
  // Delegate transport/cookies/HMAC/abort to the existing viewGet. We
  // intentionally type the inner call as `unknown` since the typed shape
  // is established below by safeParse.
  const raw = await viewGet<unknown>(slug, opts)

  const parsed = envelopeSchema.safeParse(raw)
  if (!parsed.success) {
    // D-33 R1: throw with structured forensics so the consumer / Sentry
    // tag / test assertion can pinpoint the drift.
    throw new ContractMismatchError({
      slug,
      expectedShape: 'viewProxyEnvelope<' + slug + '>',
      actualShape: summarizeActualShape(raw),
      rawData: raw,
      message:
        'Contract mismatch on view-proxy slug "'
        + slug
        + '": '
        + parsed.error.issues.map((i) => i.path.join('.') + ' ' + i.message).join('; '),
    })
  }

  return {
    data: parsed.data.data,
    total: parsed.data.total,
    meta: parsed.data.meta,
  }
}

/**
 * Lightweight shape summarizer — no JSON.stringify of full payloads (PII /
 * size). Reports top-level keys + the runtime typeof for each.
 */
function summarizeActualShape(value: unknown): string {
  if (value === null) return 'null'
  if (typeof value !== 'object') return typeof value
  if (Array.isArray(value)) return 'array(' + value.length + ')'
  const keys = Object.keys(value as Record<string, unknown>)
  return 'object{' + keys.slice(0, 6).join(',') + (keys.length > 6 ? ',…' : '') + '}'
}
